import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContentSpace, MainPageWithButtons } from "../../Common/_components";
import { WaitSpinner } from "../../Common/_components/WaitSpinner";
import { EXCEPTIONS } from "../../Everou/_constants";
import { Paginator } from "../../Everou/_components";
import { TimeHelper } from "../../Everou/_helpers";
import { PMSReservationMapper } from "../_mappers/PMSReservationMapper";
import { PMSReservation, canOfferEarlyCheckin, earlyCheckinOfferNotVisibleYet, getEarlyCheckinDecision, getReservationStateBadge } from "../_models/PMSIntegration";
import { EarlyCheckinDecision, PMSReservationsService } from "../_services/PMSReservationsService";
import { EarlyCheckinDecisionControls } from "../_components/EarlyCheckinDecisionControls";
import { PMSLocationsService } from "../_services/PMSLocationsService";
import {
    ReservationsFilterBar,
    RESERVATION_STATUS_ALL,
    RESERVATION_LOCATION_ALL,
    buildReservationFilterParams,
} from "../_components/ReservationsFilterBar";
import { Icons } from "../../Everou/_assets";
import { CapsuleButton, SimpleToggle } from "../../Everou/_components";
import ToolTipWrapper from "../../Everou/_components/ToolTipWrapper";
import { useGlobalNotification } from "../../Common/_hooks/useGlobalNotification";
import { appPermissions } from "../../Everou/_constants/permissions.constants";
import {
    EditGlobalReservationModal,
    EditModalMode,
} from "./_components/EditGlobalReservationModal";

const PAGE_SIZE = 20;
const DATE_FORMAT = "DD/MM/YYYY HH:mm";
const EMPTY = "—";

// Walk the paginated locations endpoint and collect every location. Only
// uid/name (dropdown), the early check-in toggle (per-row offer control), the
// timezone (the guest's public link decides visibility by the LOCATION's
// arrival day) and the caller's role (per-row edit/delete gate) are needed,
// so read them straight from the server payload (the full location mapper
// touches many optional fields and isn't worth the risk here).
const LOCATIONS_PAGE_SIZE = 50;
type ReservationLocation = {
    uid: string;
    name: string;
    earlyCheckinEnabled: boolean;
    timezone: string | null;
    permissionType: number | null;
};
async function loadAllLocations() {
    const all: ReservationLocation[] = [];
    for (let page = 0; ; page++) {
        const response = await PMSLocationsService.list({
            offset: page,
            page_size: LOCATIONS_PAGE_SIZE,
        });
        const batch = response?.locations ?? [];
        all.push(
            ...batch.map((location: any) => ({
                uid: location.uid,
                name: location.name,
                earlyCheckinEnabled:
                    location.early_checkin_enabled
                    ?? location.features?.early_checkin_enabled
                    ?? false,
                timezone: location.timezone ?? null,
                permissionType: location.permission_type ?? null,
            })),
        );
        const totalCount = response?.total_count ?? all.length;
        if (batch.length < LOCATIONS_PAGE_SIZE || all.length >= totalCount) break;
    }
    return all;
}

export function PMSReservationsModule() {

    // INITIALIZATION
    const { t } = useTranslation();
    const { notify } = useGlobalNotification();

    // LOCAL STATE
    const [offset, setOffset] = useState(0); // 0-based page index
    const [items, setItems] = useState<PMSReservation[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    // Bump to force a refetch (e.g. after an inline edit/delete) without
    // touching the filter/pagination state.
    const [reloadToken, setReloadToken] = useState(0);
    const [editTarget, setEditTarget] = useState<{
        reservation: PMSReservation;
        mode: EditModalMode;
    } | null>(null);
    const reload = () => setReloadToken(token => token + 1);

    // FILTER STATE
    // Default the start-date filter to the current instant: past reservations
    // are rarely interesting, and the filter bar's "clear dates" still reveals
    // them. The default is sent verbatim ("from now"); once the user picks a day
    // (dateFromIsDefault → false) it widens to start-of-day like any other pick.
    const timezone = TimeHelper.getCurrentTimezone();
    const [statusFilter, setStatusFilter] = useState<string>(RESERVATION_STATUS_ALL);
    const [locationFilter, setLocationFilter] = useState<string>(RESERVATION_LOCATION_ALL);
    const [dateFrom, setDateFrom] = useState<string | null>(
        () => TimeHelper.getTodayIsoString(),
    );
    const [dateFromIsDefault, setDateFromIsDefault] = useState(true);
    const [dateTo, setDateTo] = useState<string | null>(null);

    // Locations for the filter dropdown and for the per-reservation early
    // check-in gate (the offer control only shows when the reservation's
    // location has the toggle on), so they load on mount.
    const [locations, setLocations] = useState<ReservationLocation[]>([]);
    // uid → {early-check-in flag, timezone}, so each rendered row is an O(1)
    // lookup instead of scanning the whole locations list.
    const locationByUid = useMemo(() => {
        const map = new Map<string, ReservationLocation>();
        locations.forEach(location => map.set(location.uid, location));
        return map;
    }, [locations]);
    // Invitation uid whose early check-in offer request is in flight, so a
    // second click can't fire a duplicate before the first one settles.
    const [offerBusyUid, setOfferBusyUid] = useState<string | null>(null);
    // Same guard for the confirm control.
    const [confirmBusyUid, setConfirmBusyUid] = useState<string | null>(null);
    const locationsRequestedRef = useRef(false);
    const mountedRef = useRef(true);

    const listRef = useRef<HTMLDivElement>(null);

    // Track unmount so a late locations response doesn't set state on a gone
    // component.
    useEffect(() => () => { mountedRef.current = false; }, []);

    // Load every location for the filter dropdown, lazily and only once. The
    // endpoint paginates (offset is a 0-based page index) and caps the page
    // size, so walk the pages rather than asking for one huge page.
    const loadLocationsOnce = () => {
        if (locationsRequestedRef.current) return;
        locationsRequestedRef.current = true;
        loadAllLocations()
            .then(all => {
                if (mountedRef.current) setLocations(all);
            })
            .catch(() => {
                // Allow a retry on the next open; meanwhile the dropdown just
                // shows the "all" option and the reservations list still works.
                locationsRequestedRef.current = false;
            });
    };

    // The early check-in gate needs the locations upfront, not just when the
    // filter dropdown opens.
    useEffect(() => {
        loadLocationsOnce();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // DATA LOADING
    // No uid => every reservation owned by the user, already ordered by start
    // date and paginated in the backend. Filters are applied server-side.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        const filters = buildReservationFilterParams({ statusFilter, locationFilter, dateFrom, dateTo, timezone, dateFromIsDefault });
        PMSReservationsService.list({ offset, page_size: PAGE_SIZE, ...filters })
            .then(response => {
                if (cancelled) return;
                const page = PMSReservationMapper.pageServerToLocal(response);
                setItems(page.items);
                setTotalCount(page.total_count);
                setLoading(false);
            })
            .catch(err => {
                // A newer request to the same endpoint superseded this one — the
                // winning fetch will populate state, so leave the spinner to it.
                if (cancelled || err?.message === EXCEPTIONS.REQUEST_EXPIRED) return;
                setError(true);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, statusFilter, locationFilter, dateFrom, dateTo, dateFromIsDefault, reloadToken]);

    // ACTION HANDLERS
    const onChangePage = (page: number) => {
        listRef.current?.scrollIntoView({ behavior: "smooth" });
        setOffset(page);
    };

    // A filter change resets back to the first page; the fetch effect reruns.
    const onStatusChange = (value: string) => { setStatusFilter(value); setOffset(0); };
    const onLocationChange = (value: string) => { setLocationFilter(value); setOffset(0); };
    const onDateFromChange = (value: string | null) => { setDateFrom(value); setDateFromIsDefault(false); setOffset(0); };
    const onDateToChange = (value: string | null) => { setDateTo(value); setOffset(0); };
    const onClearDates = () => { setDateFrom(null); setDateTo(null); setOffset(0); };

    function premiumOnlyTooltip() {
        return (
            <ToolTipWrapper
                // Opens downwards: the title sits right under the header,
                // which is at z-index 9999, so the default upwards placement
                // left the tooltip hidden behind it.
                tooltipProps={{ place: "bottom" }}
                tooltip={
                    <span
                        style={{
                            display: "block",
                            maxWidth: "24rem",
                            textTransform: "none",
                            fontWeight: 400,
                            whiteSpace: "normal",
                        }}
                    >
                        {t("mod_pms_reservations_premium_only_tooltip")}
                    </span>
                }
            >
                <img
                    src={Icons.info}
                    alt="info"
                    className="ml-2"
                    style={{ width: "20px", height: "20px", cursor: "help" }}
                />
            </ToolTipWrapper>
        );
    }

    return (
        <>
            <ContentSpace
                mainContent={
                    <MainPageWithButtons
                        pageTitle={
                            <span className="d-inline-flex align-items-center">
                                {t("mod_pms_reservations")}
                                {premiumOnlyTooltip()}
                            </span>
                        }
                        size={"X_LARGE"}
                        content={content()}
                    />
                }
            />
            {editTarget && (
                <EditGlobalReservationModal
                    reservation={editTarget.reservation}
                    mode={editTarget.mode}
                    onClose={() => setEditTarget(null)}
                    onChanged={reload}
                />
            )}
        </>
    );

    function content() {
        const hasActiveFilters =
            statusFilter !== RESERVATION_STATUS_ALL ||
            locationFilter !== RESERVATION_LOCATION_ALL ||
            !!dateFrom ||
            !!dateTo;

        // Truly empty and never filtered → simple prompt, no filter bar.
        if (!loading && !error && !totalCount && !hasActiveFilters) {
            return (
                <div className="d-flex align-items-center justify-content-center pt-5 text-secondary">
                    <span>{t("mod_integrations_reservations_not_yet")}</span>
                </div>
            );
        }

        return (
            <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
                <ReservationsFilterBar
                    statusFilter={statusFilter}
                    onStatusChange={onStatusChange}
                    locationFilter={locationFilter}
                    onLocationChange={onLocationChange}
                    onLocationOpen={loadLocationsOnce}
                    locations={locations}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateFromChange={onDateFromChange}
                    onDateToChange={onDateToChange}
                    onClearDates={onClearDates}
                    timezone={timezone}
                />
                {body()}
            </div>
        );
    }

    function body() {
        if (loading) {
            return (
                <div className="d-flex align-items-center justify-content-center pt-5">
                    <WaitSpinner />
                </div>
            );
        }

        if (error) {
            return (
                <div
                    data-testid="reservations-error"
                    className="d-flex align-items-center justify-content-center pt-5 text-secondary"
                >
                    <span>{t("global_error")}</span>
                </div>
            );
        }

        if (!totalCount) {
            return (
                <div className="d-flex align-items-center justify-content-center pt-5 text-secondary">
                    <span>{t("mod_pms_reservation_filter_no_results")}</span>
                </div>
            );
        }

        return (
            <>
                <div ref={listRef} className="flex-grow-1 overflow-auto">
                    {table()}
                </div>
                {totalCount > PAGE_SIZE && (
                    <div className="bg-white pt-3 py-2">
                        <Paginator
                            pageSize={PAGE_SIZE}
                            total={totalCount}
                            pageOffset={offset}
                            onChangePage={onChangePage}
                        />
                    </div>
                )}
            </>
        );
    }

    function table() {
        return (
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>{t("mod_integrations_reservation_id")}</th>
                        <th>{t("global_location")}</th>
                        <th>{t("global_name")}</th>
                        <th>{t("global_email")}</th>
                        <th>{t("mod_integrations_reservation_start")}</th>
                        <th>{t("mod_integrations_reservation_end")}</th>
                        <th>{t("mod_pms_reservation_early_checkin")}</th>
                        <th>{t("mod_pms_reservation_early_checkout")}</th>
                        <th>{t("global_status")}</th>
                        <th>{t("mod_invitations_keypad_code")}</th>
                        <th>{t("global_link")}</th>
                        <th aria-label="actions" />
                    </tr>
                </thead>
                <tbody>{items.map(row)}</tbody>
            </table>
        );
    }

    function row(reservation: PMSReservation) {
        return (
            <tr key={reservation.uid} data-testid="reservation-row">
                <td>{idCell(reservation)}</td>
                <td>{reservation.locationName || EMPTY}</td>
                <td>{reservation.guestName || EMPTY}</td>
                <td>{reservation.email || EMPTY}</td>
                <td>{formatDate(reservation.reservationStart)}</td>
                <td>{formatDate(reservation.reservationEnd)}</td>
                <td>{earlyCheckinCell(reservation)}</td>
                <td>{formatDate(reservation.earlyCheckoutAt)}</td>
                <td>{statusBadge(reservation)}</td>
                <td>{reservation.keypadCode || EMPTY}</td>
                <td>{link(reservation.url)}</td>
                <td>{actionsCell(reservation)}</td>
            </tr>
        );
    }

    // Installers only see the reservations of their locations: editing and
    // deleting is for the owner and the admins, as in the backend. A row whose
    // role is unknown (no location, or one not in the list) keeps the buttons:
    // the backend is the gate, this only spares installers a dead click.
    function actionsCell(reservation: PMSReservation) {
        const rowLocation = reservation.locationUid
            ? locationByUid.get(reservation.locationUid)
            : undefined;
        const role = rowLocation?.permissionType;
        if (role != null && !appPermissions.canManageReservations(role))
            return null;

        return (
            <span
                className="d-inline-flex align-items-center"
                style={{ columnGap: "8px" }}
                data-testid="reservation-actions"
            >
                <CapsuleButton
                    size="SMALL"
                    icon={Icons.edit}
                    tooltip={t("global_edit")}
                    boostedToolTip
                    onClick={() => setEditTarget({ reservation, mode: "edit" })}
                />
                <CapsuleButton
                    size="SMALL"
                    icon={Icons.trash}
                    style="DANGER"
                    tooltip={t("global_delete")}
                    boostedToolTip
                    onClick={() =>
                        setEditTarget({ reservation, mode: "confirmDelete" })
                    }
                />
            </span>
        );
    }

    // Early check-in cell: once the guest has picked an arrival time, show it
    // with the request-state badge (requested/confirmed/rejected, or the
    // derived "expired" when nobody answered before the stay began) and the
    // decision buttons while deciding still matters; until then, show the
    // "flat is ready" offer toggle when canOfferEarlyCheckin says so.
    function earlyCheckinCell(reservation: PMSReservation) {
        const rowLocation = reservation.locationUid
            ? locationByUid.get(reservation.locationUid)
            : undefined;

        if (reservation.earlyCheckinAt) {
            const { state, canDecide } = getEarlyCheckinDecision(reservation, {
                confirmedAt: reservation.earlyCheckinConfirmedAt,
                rejectedAt: reservation.earlyCheckinRejectedAt,
            });
            if (!state) return formatDate(reservation.earlyCheckinAt);

            return (
                <span
                    className="d-inline-flex flex-column align-items-start"
                    style={{ rowGap: "4px" }}
                >
                    <span>{formatDate(reservation.earlyCheckinAt)}</span>
                    <EarlyCheckinDecisionControls
                        state={state}
                        canDecide={canDecide}
                        busy={!!confirmBusyUid}
                        stacked
                        onDecide={decision =>
                            handleEarlyCheckinDecision(reservation, decision)
                        }
                    />
                </span>
            );
        }

        const offered = !!reservation.earlyCheckinOfferedAt;
        const locationEnabled = !!rowLocation?.earlyCheckinEnabled;
        if (!canOfferEarlyCheckin(reservation, { offered, locationEnabled }))
            return EMPTY;

        // The public link only shows the offer card on the arrival day — the
        // LOCATION's arrival day, so use its timezone (viewer tz as fallback
        // while locations load) to tell the host when the activated offer is
        // stored but not yet visible.
        const notVisibleYet =
            offered &&
            earlyCheckinOfferNotVisibleYet(
                reservation,
                rowLocation?.timezone || timezone,
            );

        return (
            <span className="d-inline-flex flex-column">
                <ToolTipWrapper tooltip={t("mod_pms_reservation_early_checkin_offer")}>
                    <SimpleToggle
                        testID="early-checkin-offer-toggle"
                        className="c-toggle-smooth"
                        checked={offered}
                        readOnly={!!offerBusyUid}
                        handleChange={({ detail: { value } }) =>
                            handleOfferEarlyCheckin(reservation, value)
                        }
                    />
                </ToolTipWrapper>
                {notVisibleYet && (
                    <span className="text-secondary" style={{ fontSize: "11px" }}>
                        {t("mod_pms_reservation_early_checkin_offer_visible_on_arrival")}
                    </span>
                )}
            </span>
        );
    }

    async function handleOfferEarlyCheckin(
        reservation: PMSReservation,
        enabled: boolean,
    ) {
        if (!reservation.invitationUid || offerBusyUid) return;
        setOfferBusyUid(reservation.invitationUid);
        try {
            const response = await PMSReservationsService.setEarlyCheckinOffer({
                uid: reservation.invitationUid,
                enabled,
            });
            const offeredAt = response?.early_checkin_offered_at ?? null;
            if (!mountedRef.current) return;
            setItems(current =>
                current.map(item =>
                    item.uid === reservation.uid
                        ? { ...item, earlyCheckinOfferedAt: offeredAt }
                        : item,
                ),
            );
        } catch (e: any) {
            // ServiceHelper already maps backend error codes (e.g. 770 when
            // the location toggle is off) to a translated message.
            notify(e?.message || t("global_error"), "error");
        } finally {
            if (mountedRef.current) setOfferBusyUid(null);
        }
    }

    async function handleEarlyCheckinDecision(
        reservation: PMSReservation,
        decision: EarlyCheckinDecision,
    ) {
        if (!reservation.invitationUid || confirmBusyUid) return;
        setConfirmBusyUid(reservation.invitationUid);
        try {
            const response = await PMSReservationsService.setEarlyCheckinDecision({
                uid: reservation.invitationUid,
                decision,
            });
            const confirmedAt = response?.early_checkin_confirmed_at ?? null;
            const rejectedAt = response?.early_checkin_rejected_at ?? null;
            if (!mountedRef.current) return;
            setItems(current =>
                current.map(item =>
                    item.uid === reservation.uid
                        ? {
                              ...item,
                              earlyCheckinConfirmedAt: confirmedAt,
                              earlyCheckinRejectedAt: rejectedAt,
                          }
                        : item,
                ),
            );
        } catch (e: any) {
            notify(e?.message || t("global_error"), "error");
        } finally {
            if (mountedRef.current) setConfirmBusyUid(null);
        }
    }

    // Server datetimes are UTC; show them in the viewer's local timezone,
    // matching the per-location reservation view.
    function formatDate(value) {
        return value ? TimeHelper.isoStringToLocalTimezoneFormat(value, DATE_FORMAT) : EMPTY;
    }

    function idCell(reservation: PMSReservation) {
        return (
            <span className="d-inline-flex align-items-center" style={{ columnGap: "6px" }}>
                <span>{reservation.reservationId || EMPTY}</span>
                {reservation.isManual && (
                    <span
                        className="badge badge-pill"
                        style={{ backgroundColor: "#9b9b9b", color: "#fff" }}
                    >
                        {t("mod_pms_reservation_manual")}
                    </span>
                )}
            </span>
        );
    }

    function statusBadge(reservation: PMSReservation) {
        const badge = getReservationStateBadge(reservation);
        if (!badge) return EMPTY;
        return (
            <span
                className="badge badge-pill"
                style={{ backgroundColor: badge.color, color: "#fff" }}
            >
                {t(badge.labelKey)}
            </span>
        );
    }

    function link(url) {
        if (!url) return EMPTY;
        return (
            <a href={url} target="_blank" rel="noreferrer">
                {t("global_link")}
            </a>
        );
    }
}
